import clsx from 'clsx';
import { FC, useState } from 'react';
import { useFormikContext } from 'formik';
import { Card } from '../../../base-components/Card';
import FormCheckbox from '../../../base-components/Checkbox/FormCheckbox';
import { Collapsible } from '../../../base-components/Collapsible';
import { FormDateTimePicker } from '../../../base-components/DateTimePicker/FormDateTimePicker';
import { Delimiter } from '../../../base-components/Delimiter';
import { FormNumberInput } from '../../../base-components/FormNumberInput';
import { FormTextInput } from '../../../base-components/FormTextInput';
import { CollapseButton } from '../components/CollapseButton';
import { FormSelectRegionModal } from '../components/FormSelectRegionModal';
import { ResetButton } from '../components/ResetButton';
import { Row } from '../components/Row';
import { SearchButton } from '../components/SearchButton';
import { FormSelectBlock } from '../components/SelectBlock';
import { FormSelectOkpd2 } from '../components/SelectOkpd2';
import { FormSelectProcedureTemplate } from '../components/SelectProcedureTemplate';
import { FormSelectRegions } from '../components/SelectRegion/FormSelectRegions';
import { FormSelectSection } from '../components/SelectSection';
import { FormSelectTariff } from '../components/SelectTariff';
import { PurchasesState } from '../Purchases.type';
import { setEndOfDay } from '../utils';
import { setStartOfDay } from '../utils';
import styles from './Filter.module.scss';
import { Reference } from './Filter.type';
import { SelectableOptions } from './Filter.type';
import { Matches } from './Filter.type';
import { useSyncQueryWithFilter } from '../hooks/useQueryParams';

const Period = () => <Delimiter className={styles.period}>-</Delimiter>;

export interface FilterProps {
    reference: Reference;
    selectableOptions: SelectableOptions;
    matches: Matches;

    emptyFilterState: PurchasesState;

    isRegionsModalVisible: boolean;
    showRegionsModal: () => void;
    hideRegionsModal: () => void;
}

export const Filter: FC<FilterProps> = ({
    reference,
    selectableOptions,
    matches,
    emptyFilterState,
    isRegionsModalVisible,
    showRegionsModal,
    hideRegionsModal,
}) => {
    const [collapsed, setCollapsed] = useState(true);
    const { setValues } = useFormikContext<PurchasesState>();

    useSyncQueryWithFilter(setValues);

    return (
        <Card className={styles.card}>
            <Row>
                <FormTextInput
                    name="search"
                    placeholder="Введите номер или наименование закупки на ЕСТП или ЕИС, ИНН, название заказчика"
                    appearance="compact"
                    className={styles.control}
                />
            </Row>
            <Collapsible isCollapsed={collapsed}>
                <Row className={styles.breakpointB}>
                    <FormSelectRegions
                        name="region"
                        instanceId="select-region"
                        regions={selectableOptions.regions}
                        className={clsx(styles.control, styles.region)}
                        onPlusClick={showRegionsModal}
                        appearance="compact"
                        matches={matches?.regions}
                    />
                    <Delimiter />
                    <FormSelectOkpd2
                        name="okpd2"
                        instanceId="select-okpd2"
                        className={clsx(styles.control, styles.okpd2)}
                        appearance="compact"
                    />
                </Row>
                <Row className={styles.breakpointC}>
                    <FormSelectSection
                        name="section"
                        instanceId="select-section"
                        sections={selectableOptions.sections}
                        className={clsx(styles.control, styles.section)}
                        appearance="compact"
                        matches={matches?.sections}
                    />
                    <Delimiter />
                    <FormSelectTariff
                        name="tariff"
                        instanceId="select-tariff"
                        tariffs={selectableOptions.tariffs}
                        className={clsx(styles.control, styles.tariff)}
                        appearance="compact"
                        matches={matches?.tariffs}
                    />
                </Row>
                <Row className={styles.breakpointA}>
                    <FormSelectBlock
                        name="block"
                        instanceId="select-block"
                        blocks={selectableOptions.blocks}
                        className={clsx(styles.control)}
                        appearance="compact"
                        matches={matches?.blocks}
                    />
                    <Delimiter />
                    <Row className={styles.breakpointC}>
                        <FormDateTimePicker
                            name="publishedFrom"
                            className={clsx(
                                styles.control,
                                styles.publishedFrom
                            )}
                            placeholder="Дата публикации с"
                            format="date"
                            appearance="compact"
                            setValue={setStartOfDay}
                        />
                        <Period />
                        <FormDateTimePicker
                            name="publishedTo"
                            className={clsx(styles.control, styles.publishedTo)}
                            placeholder="по"
                            format="date"
                            appearance="compact"
                            setValue={setEndOfDay}
                        />
                    </Row>
                </Row>
                <Row className={styles.breakpointA}>
                    <Row className={styles.breakpointC}>
                        <FormDateTimePicker
                            name="receiveStartFrom"
                            className={clsx(
                                styles.control,
                                styles.receiveStartFrom
                            )}
                            placeholder="Начало подачи заявок с"
                            format="date"
                            appearance="compact"
                            setValue={setStartOfDay}
                        />
                        <Period />
                        <FormDateTimePicker
                            name="receiveStartTo"
                            className={clsx(
                                styles.control,
                                styles.receiveStartTo
                            )}
                            placeholder="по"
                            format="date"
                            appearance="compact"
                            setValue={setEndOfDay}
                        />
                    </Row>
                    <Delimiter />
                    <Row className={styles.breakpointC}>
                        <FormDateTimePicker
                            name="receiveFinishFrom"
                            className={clsx(
                                styles.control,
                                styles.receiveFinishFrom
                            )}
                            placeholder="Окончание подачи заявок с"
                            format="date"
                            appearance="compact"
                            setValue={setStartOfDay}
                        />
                        <Period />
                        <FormDateTimePicker
                            name="receiveFinishTo"
                            className={clsx(
                                styles.control,
                                styles.receiveFinishTo
                            )}
                            placeholder="по"
                            format="date"
                            appearance="compact"
                            setValue={setEndOfDay}
                        />
                    </Row>
                </Row>
                <Row className={styles.breakpointA}>
                    <Row className={styles.breakpointC}>
                        <FormNumberInput
                            name="startPriceFrom"
                            placeholder="Сумма НМЦ от"
                            precision={0}
                            className={clsx(
                                styles.control,
                                styles.startPriceFrom
                            )}
                            appearance="compact"
                        />
                        <Period />
                        <FormNumberInput
                            name="startPriceTo"
                            placeholder="до"
                            precision={0}
                            className={clsx(
                                styles.control,
                                styles.startPriceTo
                            )}
                            appearance="compact"
                        />
                    </Row>
                    <Delimiter />
                    <Row className={styles.breakpointC}>
                        <FormSelectProcedureTemplate
                            name="procedureTemplate"
                            instanceId="select-procedure-template"
                            procedureTemplates={
                                selectableOptions.procedureTemplates
                            }
                            className={clsx(
                                styles.control,
                                styles.procedureTemplate
                            )}
                            appearance="compact"
                            matches={matches?.procedureTemplates}
                        />
                        <Period />
                        <div
                            className={clsx(
                                styles.control,
                                styles.freeParticipation
                            )}
                        >
                            <FormCheckbox
                                name="freeParticipation"
                                label="Бесплатное участие"
                            />
                        </div>
                    </Row>
                </Row>
            </Collapsible>

            <Row className={styles.breakpointB}>
                <Row className={styles.breakpointC}>
                    <CollapseButton
                        collapsed={collapsed}
                        onClick={() => setCollapsed(!collapsed)}
                        className={styles.collapse}
                    />
                </Row>
                <Row className={clsx(styles.actions, styles.breakpointC)}>
                    <ResetButton
                        emptyFilterState={emptyFilterState}
                        className={styles.reset}
                    />
                    <Delimiter />
                    <SearchButton className={styles.submit} />
                </Row>
            </Row>

            {isRegionsModalVisible && (
                <FormSelectRegionModal
                    name="region"
                    regions={reference.regions}
                    districts={reference.districts}
                    onHide={hideRegionsModal}
                    matches={matches?.regions}
                />
            )}
        </Card>
    );
};
